import React, { Fragment, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import bemCn from 'bem-cn';
import loadable from '@loadable/component';
import { BLOG_TYPES } from '../../consts/blog-system.consts';
import { SIGN_UP_SRC } from '../../consts/profile.const';
import { IMG_RESIZES } from '../../consts/resizer.consts';
import { ImageHelper } from '../../classes/image-helper.class';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { getInitState } from '../../helpers/spot-slug';
import { escapeString } from '../../helpers/escape-string';
import { DEFAULT_FAQS } from '../../consts/blog-dog-trainers.consts';
import { RouteFormatter } from '../../routes';
import { COOKIE_PARAM_NAME } from '../../consts/cookies.consts';
import { getFirstNameInitial } from '../../helpers/name';
import { BlogAuthorDetails } from '../../components/blogs/author-details';
import BlogArticleDesktopLow from '../../components/blog-article-desktop-low';
import { Banner1, Banner2 } from '../../components/blogs/banners';
import { BlogSubDetail } from '../../components/blogs/sub-details';
import BlogArticleSimple from '../../components/blog-article-simple';
import SpotBreadcrumbs from '../../components/spot-breadcrumbs';
import BlogCategories from '../../components/blog-categories';
import BlogTopSticky from '../../components/blog-top-nav/top-sticky';
import BlogSideBar from '../../components/blog-side-bar';
import SniffButton from '../../components/sniff-button';
import BlogFooter from '../../components/blog-footer';
import BlogTopNav from '../../components/blog-top-nav';
import PictureSet from '../../components/picture-set';
import Preloader from '../../components/preloader';
import SnifHr from '../../components/snif-hr';
import SvgChevronRight from 'src/assets/svg/SvgChevronRight.svg';
import SvgChevronLeft from 'src/assets/svg/SvgChevronLeft.svg';
import SvgTopBadge from 'src/assets/svg/SvgTopBadge.svg';
import SvgPaw from 'src/assets/svg/SvgPaw.svg';
import SvgPin from 'src/assets/svg/SvgPin.svg';
import '../blog/post.scss';
import './style.scss';

const FooterNew = loadable(() => import('../../components/footer-new'));
const ModalMobile = loadable(() => import('../../components/modal-mobile'));
const MobileSubPage = loadable(() => import('../../components/mobile-sub-page'));

const CookieService = require('../../services/cookie.service');
const useGetBlogCategories = require('../../services/hooks/useGetBlogCategories');
const useGetDogTrainersPage = require('../../services/hooks/useGetDogTrainersPage');
const useGetDogTrainersTestiPagination = require('../../services/hooks/useGetDogTrainersTestiPagination');

const b = bemCn('sniff-blog-post');
const bDog = bemCn('sniff-dog-trainers');

const RES_TOP_LIMIT = 20;

const types = {
    CITY: 'CITY',
    STATE: 'STATE',
    COUNTRY: 'COUNTRY',
    PROFILE: 'PROFILE',
    CATEGORY: 'CATEGORY',
    CATEGORY_CITY: 'CATEGORY_CITY',
};

const countryRankP = [
    'aggressiveDogTrainingRank',
    'deafDogTrainingRank',
    'inHomeTrainingRank',
    'protectionGuardDogTrainingRank',
    'reactiveDogTrainingRank',
    'serviceDogTrainingRank',
    'boardAndTrainRank',
    'offLeashTrainingRank',
    'puppyTrainingRank',
    'agilityTrainingRank',
    'herdingTrainingRank',
];

const cityRankP = [
    'aggressiveDogTrainingCityRank',
    'deafDogTrainingCityRank',
    'inHomeTrainingCityRank',
    'protectionGuardDogTrainingCityRank',
    'reactiveDogTrainingCityRank',
    'serviceDogTrainingCityRank',
    'boardAndTrainCityRank',
    'offLeashTrainingCityRank',
    'puppyTrainingCityRank',
    'agilityTrainingCityRank',
    'herdingTrainingCityRank',
];

const CAT_TYPE = {
    'Aggressive dog training': {
        trainers: 'Aggressive Dog Trainers',
        training: 'Aggressive Dog Training',
    },
    'Agility training': {
        trainers: 'Dog Agility Trainers',
        training: 'Dog Agility Training',
    },
    'Board and train': {
        trainers: 'Board and Train Dog Trainers',
        training: 'Board and Train Dog Training',
    },
    'Deaf dog training': {
        trainers: 'Deaf Dog Trainers',
        training: 'Deaf Dog Training',
    },
    'Herding training': {
        trainers: 'Herding Dog Trainers',
        training: 'Herding Dog Training',
    },
    'In home training': {
        trainers: 'In Home Dog Trainers',
        training: 'In Home Dog Training',
    },
    'Off leash training': {
        trainers: 'Off Leash Dog Trainers',
        training: 'Off Leash Dog Training',
    },
    'Protection/guard dog training': {
        trainers: 'Protection/Guard Dog Trainers',
        training: 'Protection/Guard Dog Training',
    },
    'Puppy training': {
        trainers: 'Puppy Trainers',
        training: 'Puppy Training',
    },
    'Reactive dog training': {
        trainers: 'Reactive Dog Trainers',
        training: 'Reactive Dog Training',
    },
    'Service dog training': {
        trainers: 'Service Dog Trainers',
        training: 'Service Dog Training',
    },
};

const slugify = (str, country) => {
    return str
        .replace(country ? /Rank$/ : /CityRank$/, '')
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .toLowerCase()
        .replace(/-$/, '');
};

const readafy = (str, country) => {
    return str
        .replace(country ? /Rank$/ : /CityRank$/, '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z])/g, (match) => match.toLowerCase())
        .replace(/^./, (match) => match.toUpperCase());
};

const Divider = () => (
    <div className="py-4">
        <SnifHr w100 />
    </div>
);

const getBread = ({ city, pageType, query, fullName, slug, state, category }) => {
    const bread = [
        { name: 'Blog', url: RouteFormatter.blog({}) },
        { name: 'Dog Trainers', url: RouteFormatter.blogTrainersUS() },
    ];

    if (pageType == types.STATE) {
        bread.push({ name: state, url: RouteFormatter.blogTrainersSub({ slug: query || slug }) });
    } else if (pageType == types.CITY) {
        const fixState = state?.trim().replaceAll('.', '').replaceAll(' ', '-').toLowerCase();
        bread.push({ name: state, url: RouteFormatter.blogTrainersSub({ slug: fixState }) });
        bread.push({ name: city });
    } else if (pageType == types.CATEGORY) {
        bread.push({ name: category });
    } else if (pageType == types.PROFILE) {
        const fixState = state?.trim().replaceAll('.', '').replaceAll(' ', '-').toLowerCase();
        const fixCity = city?.trim().replaceAll('.', '').replaceAll(' ', '-').toLowerCase();
        const initState = state ? getInitState(state) : '';
        const citySlug = `${fixCity}-${initState.toLowerCase()}`;
        const canShowCity = !!fixCity && fixCity != 'none';
        bread.push({ name: state, url: RouteFormatter.blogTrainersSub({ slug: fixState }) });
        canShowCity && bread.push({ name: city, url: RouteFormatter.blogTrainersSub({ slug: citySlug }) });
        bread.push({ name: fullName });
    } else if (pageType == types.CATEGORY_CITY) {
        const fixState = state?.trim().replaceAll('.', '').replaceAll(' ', '-').toLowerCase();
        const fixCity = city?.trim().replaceAll('.', '').replaceAll(' ', '-').toLowerCase();
        const initState = state ? getInitState(state) : '';
        const citySlug = `${fixCity}-${initState.toLowerCase()}`;
        const canShowCity = !!fixCity && fixCity != 'none';
        bread.push({ name: state, url: RouteFormatter.blogTrainersSub({ slug: fixState }) });
        canShowCity && bread.push({ name: city, url: RouteFormatter.blogTrainersSub({ slug: citySlug }) });
        bread.push({ name: category });
    }

    return bread;
};

const RenderRanks = ({ countryRank, stateRank, cityRank, city, simple, state, citySlug }) => {
    const cRanks = countryRank ? (
        <>
            #{countryRank} in{' '}
            <a href={RouteFormatter.blogTrainersUS()} className="text-underline">
                United States
            </a>
        </>
    ) : null;
    const sRanks =
        state && stateRank ? (
            <>
                #{stateRank} in{' '}
                <a href={RouteFormatter.blogTrainersSub({ slug: escapeString(state) })} className="text-underline">
                    {state}
                </a>
            </>
        ) : null;
    const ciRanks =
        city && cityRank ? (
            <>
                #{cityRank} in{' '}
                <a href={RouteFormatter.blogTrainersSub({ slug: citySlug })} className="text-underline">
                    {city}
                </a>
            </>
        ) : null;
    const ranks = [];
    cRanks && ranks.push(cRanks);
    sRanks && ranks.push(sRanks);
    ciRanks && ranks.push(ciRanks);

    if (ranks.length == 0) {
        return '';
    }

    return (
        <p className={simple ? '' : 'text-primary'}>
            {ranks.map((r, i) => (
                <React.Fragment key={i}>
                    {r}
                    {i < ranks.length - 1 ? ', ' : ''}
                </React.Fragment>
            ))}
        </p>
    );
};

const RenderListState = ({ col = 'mycol-3', myTitle, pageType, states }) => {
    const title = myTitle || 'Top Trainers By State';
    const myNote =
        pageType == types.CITY
            ? 'Ordered alphabetically, only included states where there is enough data to compile a list'
            : 'Note: we only include states where we have enough data to compile a solid list';
    const canShowNote = pageType == types.CITY || pageType == types.COUNTRY;

    return (
        <>
            <h2 className="snif-m1 snif-l2-pc snif-semibold mb-2">{title}</h2>
            {canShowNote && <p className={bDog('p-lora').mix('mb-2')}>{myNote}</p>}
            <ul className={bDog('state-link').mix(col)}>
                {states?.map((s, i) => (
                    <li key={i}>
                        <a href={RouteFormatter.blogTrainersSub({ slug: s.slug })} className="link-style">
                            {s.name || s.state}
                        </a>
                    </li>
                ))}
            </ul>
        </>
    );
};

const RenderListCity = ({ cities, col = 'mycol-2', pageType, myTitle }) => {
    const title = myTitle || 'Top Trainers By City';

    return (
        <>
            <h2 className="snif-m1 snif-l2-pc snif-semibold mb-2">{title}</h2>
            {pageType == types.COUNTRY && (
                <p className={bDog('p-lora').mix('mb-2')}>
                    Note: we only include the top 10 cities where we have enough data to compile a solid list
                </p>
            )}
            {pageType == types.CITY && (
                <p className={bDog('p-lora').mix('mb-2')}>
                    Ordered alphabetically, only included cities where there is enough data to compile a list
                </p>
            )}
            {pageType == types.CATEGORY && (
                <p className={bDog('p-lora').mix('mb-2')}>Note: We only include metros where we have enough data to compile a solid list</p>
            )}
            <ul className={bDog('city-link').mix(col)}>
                {cities?.map((s, i) => {
                    const splitSlug = s?.slug?.split('/')[0]?.split('-');
                    const shortState = splitSlug.at(-1).toUpperCase();
                    const name = `${s.name || s.city}, ${shortState}`;

                    return (
                        <li key={i}>
                            <a href={RouteFormatter.blogTrainersSub({ slug: s.slug })} rel={s?.noFollow ? 'nofollow' : ''} className="link-style">
                                {name}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

const RenderListCategory = ({ categories, col = 'mycol-2', myTitle, otherCategories }) => {
    const title = myTitle || 'Dog Trainer Types';
    const myCats = otherCategories || categories;

    return (
        <>
            <h2 className="snif-m1 snif-l2-pc snif-semibold mb-2">{title}</h2>
            <ul className={bDog('city-link').mix(col)}>
                {myCats.map((s, i) => (
                    <li key={i}>
                        <a href={RouteFormatter.blogTrainersSub({ slug: s.slug })} className="link-style">
                            {s.name || s.specialization}
                        </a>
                    </li>
                ))}
            </ul>
        </>
    );
};

const RenderListTrainer = ({ col = 'mycol-2', myTitle, trainers }) => {
    return (
        <>
            <h2 className="snif-m1 snif-l2-pc snif-semibold mb-2">{myTitle}</h2>
            <ul className={bDog('city-link').mix(col)}>
                {trainers.map((s, i) => (
                    <li key={i}>
                        <a href={RouteFormatter.blogTrainersSub({ slug: s.slug })} className="link-style">
                            {s.fullName}
                        </a>
                    </li>
                ))}
            </ul>
        </>
    );
};

const RenderListRankCountry = ({ blogPost, myCountryRanks }) => {
    if (myCountryRanks.length == 0) {
        return '';
    }

    return (
        <>
            <p>United States</p>
            <ul>
                {myCountryRanks.map((r) => {
                    const text = readafy(r, true);
                    const slug = slugify(r, true);

                    return (
                        <li key={r}>
                            #{blogPost[r]} in <a href={RouteFormatter.blogTrainersSub({ slug })}>{text}</a>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

const RenderListRankCity = ({ blogPost, myCityRanks, citySlug }) => {
    if (blogPost?.city && myCityRanks.length == 0) {
        return '';
    }

    return (
        <>
            <p>{blogPost?.city}</p>
            <ul>
                {myCityRanks.map((r) => {
                    const text = readafy(r, false);
                    const slug = `${citySlug}/${slugify(r, false)}`;

                    return (
                        <li key={r}>
                            #{blogPost[r]} in <a href={RouteFormatter.blogTrainersSub({ slug })}>{text}</a>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

const faqsTitle = ({ city, pageType, category, state }) => {
    let title = 'Sniffspot Dog Training Frequently Asked Questions';

    switch (pageType) {
        case types.CITY:
            title = `Sniffspot ${city} Dog Training Frequently Asked Questions`;
            break;
        case types.STATE:
            title = `Sniffspot ${state} Dog Training Frequently Asked Questions`;
            break;
        case types.CATEGORY:
            title = `Frequently Asked Questions About ${CAT_TYPE[category]?.training}`;
            break;
        case types.CATEGORY_CITY:
            title = `Frequently Asked Questions About ${CAT_TYPE[category]?.training} in ${city}`;
            break;
        default:
            break;
    }

    return title;
};

const RenderFaqs = ({ city, customFaqTitle, faqContent, faqTitle, pageType, category, state }) => {
    const title = customFaqTitle || faqTitle || faqsTitle({ city, pageType, category, state });

    return (
        <>
            <h2 className="snif-m1 snif-l2-pc snif-semibold mb-2">{title}</h2>
            <div itemScope itemType="https://schema.org/FAQPage" className={bDog('faqs')}>
                {faqContent.map((f, i) => (
                    <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                        <h3 className="snif-m2 snif-semibold mt-3 mb-1" itemProp="name">
                            {f.question}
                        </h3>
                        <div className="snif-p" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                            <div itemProp="text" dangerouslySetInnerHTML={{ __html: f.answer }} />
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

const RenderTrainersItem = ({ res }) => {
    const avatarUrl = ImageHelper.getUrlFromOptions(res?.avatar, `fit-in/${IMG_RESIZES.x512}`);
    const profileUrl = RouteFormatter.blogTrainersSub({ slug: res?.slug });

    return (
        <li className={bDog('state_list_item')}>
            <PictureSet imgs={avatarUrl} width={256} height={256} alt={`${res?.fullName} Photo`} white />
            <h3 className="snif-m2 snif-m1-pc snif-semibold mt-3 mb-05">
                <a href={profileUrl} target="_blank" rel="noopener noreferrer">
                    {res?.fullName}
                </a>
            </h3>
            <p className={`${bDog('p-lora')} ${bDog('state_list_item_bio')}`} dangerouslySetInnerHTML={{ __html: res?.bio }} />
            <p className="mt-2">
                <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="snif-s1 snif-medium text-primary text-underline">
                    View profile
                </a>
            </p>
        </li>
    );
};

const RenderOtherState = ({ st }) => {
    if (st?.trainers?.length < 1) {
        return '';
    }

    return (
        <div className={bDog('state')}>
            <h2 className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-3">
                <a href={RouteFormatter.blogTrainersSub({ slug: st.slug })}>The Best Dog Trainers In {st.name || st.state}</a>
            </h2>
            <ul className={bDog('state_list')}>
                {st?.trainers?.map((res, i) => (
                    <RenderTrainersItem key={i} {...{ res }} />
                ))}
            </ul>
        </div>
    );
};

const RenderOtherCity = ({ st, thisYear }) => {
    const splitSlug = st?.slug?.split('/')[0]?.split('-');
    const shortState = splitSlug?.at(-1).toUpperCase();
    const cityText = `${st.name || st.city}, ${shortState}`;

    if (st?.trainers?.length < 1) {
        return '';
    }

    return (
        <div className={bDog('state')}>
            <h2 className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-3">
                <a href={RouteFormatter.blogTrainersSub({ slug: st.slug })}>
                    The Best {cityText} Dog Trainers In {thisYear}
                </a>
            </h2>
            <ul className={bDog('state_list')}>
                {st?.trainers?.map((res, i) => (
                    <RenderTrainersItem key={i} {...{ res }} />
                ))}
            </ul>
        </div>
    );
};

const RenderRemainTrainers = ({ blogPost, remainTrainers }) => (
    <div>
        <h2 className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-2 mb-md-3">See More Trainers In {blogPost.state}</h2>
        <ul className={bDog('city-link').mix('remain')}>
            {remainTrainers?.map((s, i) => {
                return (
                    <li key={i}>
                        <a href={s.slug} target="_blank" rel="noopener noreferrer" className="link-style">
                            {s.fullName}
                        </a>
                    </li>
                );
            })}
        </ul>
        <Divider />
    </div>
);

const RenderProfile = ({ res, useStateSSR }) => {
    const avatarUrl = ImageHelper.getUrlFromOptions(res.avatar, `fit-in/${IMG_RESIZES.x512}`);
    const profileUrl = RouteFormatter.blogTrainersSub({ slug: res.slug });
    const servicesProvided = res.services?.join(', ');
    const certs = res.certifications?.join(', ');

    const handleProfile = (e) => {
        e.preventDefault();
        window.open(profileUrl, '_blank', 'noopener,noreferrer');
    };

    const handleTesti = (e) => {
        e.stopPropagation();
        useStateSSR && CookieService.default.set(COOKIE_PARAM_NAME.SIGN_UP_FROM, SIGN_UP_SRC.DOGTRAINERS_WEB, { expires: 1 });
        window.location = RouteFormatter.testiNewDogTrainers({ slug: res.slug });
    };

    return (
        <li className={bDog('profile')} onClick={handleProfile}>
            <PictureSet imgs={avatarUrl} width={150} height={150} alt={`${res?.fullName} Photo`} white />
            <div>
                <a href={profileUrl} target="_blank" rel="noopener noreferrer">
                    <h2 className="snif-m2 snif-semibold text-capitalize">{res.fullName}</h2>
                </a>
                {res.certifications?.length > 0 && (
                    <div className={bDog('profile_sub')}>
                        <div className={bDog('profile_sub_svg')}>
                            <SvgTopBadge />
                        </div>
                        <p>{certs}</p>
                    </div>
                )}
                {res.services?.length > 0 && (
                    <div className={bDog('profile_sub')}>
                        <div className={bDog('profile_sub_svg')}>
                            <SvgPaw />
                        </div>
                        <p>{servicesProvided}</p>
                    </div>
                )}
                {res.serviceAreas && (
                    <div className={bDog('profile_sub')}>
                        <div className={bDog('profile_sub_svg')}>
                            <SvgPin />
                        </div>
                        <p>{res.serviceAreas}</p>
                    </div>
                )}
                <p className={bDog('profile_bio')} dangerouslySetInnerHTML={{ __html: res.bio }} />
                {res.reviews?.length > 0 && (
                    <div>
                        <div className="my-4">
                            <SnifHr w100 />
                        </div>
                        {res.reviews.map((rev, idx) => (
                            <div key={rev.id} className={idx == 1 ? 'mt-4 mt-md-3' : ''}>
                                <p className={bDog('p-lora').mix('mb-2')}>“{rev.testimonial}”</p>
                                <p className="snif-p snif-semibold">— {rev.providedBy?.nameAndInital || getFirstNameInitial(rev.reviewerName)}</p>
                            </div>
                        ))}
                    </div>
                )}
                <SniffButton className="mt-3" color="secondary" onClick={handleTesti}>
                    Write a testimonial
                </SniffButton>
            </div>
        </li>
    );
};

const RenderContentCountry = ({ blogPost, faqContent, thisYear, useStateSSR }) => {
    return (
        <div className="mx-2 mx-md-0">
            <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc mb-3 mb-md-4">The Best Dog Trainers in the United States of {thisYear}</h1>
            <BlogSubDetail {...{ blogPost, updatedAt, type: BLOG_TYPES.DOG_TRAINERS.id }} />
            <div className="mb-4">
                <p className={bDog('p-lora')}>
                    This is a list of the top dog trainers in the United States, based on votes from the Sniffspot community and the general public.
                </p>
                <ul className={bDog('list')}>
                    {blogPost?.trainers?.map((res, i) => (
                        <React.Fragment key={i}>
                            <RenderProfile {...{ res, useStateSSR }} />
                            <Divider />
                        </React.Fragment>
                    ))}
                </ul>
                {blogPost?.states?.map((st, i) => (
                    <RenderOtherState key={i} {...{ st }} />
                ))}
                <Divider />
                <div className={bDog('long-desc')} dangerouslySetInnerHTML={{ __html: blogPost?.topSectionDesc }} />
                <Banner1 />
                {blogPost?.states?.length > 0 && (
                    <>
                        <RenderListState {...{ ...blogPost }} />
                        <Divider />
                    </>
                )}
                {blogPost?.cities?.length > 0 && (
                    <>
                        <RenderListCity {...{ ...blogPost }} />
                        <Divider />
                    </>
                )}
                {blogPost?.categories?.length > 0 && (
                    <>
                        <RenderListCategory {...{ ...blogPost }} />
                        <Divider />
                    </>
                )}
                <RenderFaqs {...{ ...blogPost, faqContent }} />
                <Banner2 />
            </div>
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_TRAINERS.id }} noMargin />
            <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
        </div>
    );
};

const RenderContentState = ({ blogPost, faqContent, thisYear, useStateSSR }) => {
    const myState = blogPost?.state;
    const titleOtherCities = `See The Best Dog Trainers By City In ${myState}`;
    const titleOtherStates = `See The Best Dog Trainers In Other States Near ${myState}`;
    const canShowMoreRes = blogPost?.trainers?.length > RES_TOP_LIMIT;
    const top20Res = canShowMoreRes ? blogPost?.trainers?.slice(0, RES_TOP_LIMIT) : blogPost?.trainers;
    const remainTrainers = canShowMoreRes ? blogPost?.trainers?.slice(RES_TOP_LIMIT).sort((a, b) => a.slug?.localeCompare(b.slug)) : [];

    return (
        <div className="mx-2 mx-md-0">
            <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc mb-3 mb-md-4">
                The Best Dog Trainers in {myState} of {thisYear}
            </h1>
            <BlogSubDetail {...{ blogPost, updatedAt, type: BLOG_TYPES.DOG_TRAINERS.id }} />
            <div className="mb-4">
                <p className={bDog('p-lora')}>
                    This is a list of the top dog trainers in {myState}, based on votes from the Sniffspot community and the general public.
                </p>
                <ul className={bDog('list')}>
                    {top20Res?.map((res, i) => (
                        <React.Fragment key={i}>
                            <RenderProfile {...{ res, useStateSSR }} />
                            <Divider />
                        </React.Fragment>
                    ))}
                </ul>
                {remainTrainers.length > 0 && <RenderRemainTrainers {...{ blogPost, remainTrainers }} />}
                {blogPost?.cities?.map((st, i) => (
                    <RenderOtherCity key={i} {...{ st, thisYear }} />
                ))}
                <Divider />
                <div className={bDog('long-desc')} dangerouslySetInnerHTML={{ __html: blogPost?.topSectionDesc }} />
                <Banner1 />
                {blogPost?.cities?.length > 0 && (
                    <>
                        <RenderListCity {...{ ...blogPost, myTitle: titleOtherCities }} />
                        <Divider />
                    </>
                )}
                {blogPost?.states?.length > 0 && (
                    <>
                        <RenderListState {...{ ...blogPost, myTitle: titleOtherStates }} />
                        <Divider />
                    </>
                )}
                <RenderFaqs {...{ ...blogPost, faqContent }} />
                <Banner2 />
            </div>
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_TRAINERS.id }} noMargin />
            <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
        </div>
    );
};

const RenderContentCity = ({ blogPost, faqContent, thisYear, useStateSSR }) => {
    const splitSlug = blogPost?.slug?.split('/')[0]?.split('-');
    const shortState = splitSlug?.at(-1).toUpperCase();
    const cityText = `${blogPost.city}, ${shortState}`;
    const titleOtherCities = `See The Best Dog Trainers In Other Cities Near ${blogPost?.city}`;
    const titleOtherStates = `See The Best Dog Trainers In Other States Near ${blogPost?.state}`;
    const titleOtherCategories = `See The Best Dog Trainers By Type In ${blogPost?.city}`;

    return (
        <div className="mx-2 mx-md-0">
            <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc mb-3 mb-md-4">
                The Best Dog Trainers in {cityText} of {thisYear}
            </h1>
            <BlogSubDetail {...{ blogPost, updatedAt, type: BLOG_TYPES.DOG_TRAINERS.id }} />
            <div className="mb-4">
                <p className={bDog('p-lora')}>
                    This is a list of the top dog trainers in {cityText}, based on votes from the Sniffspot community and the general public.
                </p>
                <ul className={bDog('list')}>
                    {blogPost?.trainers?.map((res, i) => (
                        <React.Fragment key={i}>
                            <RenderProfile {...{ res, useStateSSR }} />
                            <Divider />
                        </React.Fragment>
                    ))}
                </ul>
                <div className={bDog('long-desc')} dangerouslySetInnerHTML={{ __html: blogPost?.topSectionDesc }} />
                <Banner1 />
                {blogPost?.cities?.length > 0 && (
                    <>
                        <RenderListCity {...{ ...blogPost, myTitle: titleOtherCities }} />
                        <Divider />
                    </>
                )}
                {blogPost?.states?.length > 0 && (
                    <>
                        <RenderListState {...{ ...blogPost, col: 'mycol-2', myTitle: titleOtherStates }} />
                        <Divider />
                    </>
                )}
                {blogPost?.categories?.length > 0 && (
                    <>
                        <RenderListCategory {...{ ...blogPost, myTitle: titleOtherCategories }} />
                        <Divider />
                    </>
                )}
                <RenderFaqs {...{ ...blogPost, faqContent }} />
                <Banner2 />
            </div>
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_TRAINERS.id }} noMargin />
            <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
        </div>
    );
};

const RenderContentCategory = ({ blogPost, faqContent, thisYear, useStateSSR }) => {
    const title = `The Best ${CAT_TYPE[blogPost?.category]?.trainers} in ${thisYear}`;
    const titleOtherCities = `See the Best ${CAT_TYPE[blogPost?.category]?.trainers} by City`;
    const titleOtherCategories = 'See the Best Dog Trainers by Type in the United States';

    return (
        <div className="mx-2 mx-md-0">
            <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc mb-3 mb-md-4">{title}</h1>
            <BlogSubDetail {...{ blogPost, updatedAt, type: BLOG_TYPES.DOG_TRAINERS.id }} />
            <div className="mb-4">
                <p className={bDog('p-lora')}>
                    Welcome to the Sniffspot list of dog trainers who specialize in {blogPost?.category}. Based on feedback and votes from our{' '}
                    {blogPost?.category} community, we've curated a list of top-rated dog trainers in the US.
                </p>
                <ul className={bDog('list')}>
                    {blogPost?.trainers?.map((res, i) => (
                        <React.Fragment key={i}>
                            <RenderProfile {...{ res, useStateSSR }} />
                            <Divider />
                        </React.Fragment>
                    ))}
                </ul>
                <div className={bDog('long-desc')} dangerouslySetInnerHTML={{ __html: blogPost?.topSectionDesc }} />
                <Banner1 />
                {blogPost?.cities?.length > 0 && (
                    <>
                        <RenderListCity {...{ ...blogPost, col: 'mycol-3', myTitle: titleOtherCities }} />
                        <Divider />
                    </>
                )}
                {blogPost?.categories?.length > 0 && (
                    <>
                        <RenderListCategory {...{ ...blogPost, myTitle: titleOtherCategories }} />
                        <Divider />
                    </>
                )}
                <RenderFaqs {...{ ...blogPost, faqContent }} />
                <Banner2 />
            </div>
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_TRAINERS.id }} noMargin />
            <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
        </div>
    );
};

const RenderContentCategoryCity = ({ blogPost, faqContent, thisYear, useStateSSR }) => {
    const splitSlug = blogPost?.slug?.split('/');
    const citySlug = splitSlug[0]?.split('-');
    const shortState = citySlug?.at(-1).toUpperCase();
    const cityText = `${blogPost?.city}, ${shortState}`;
    const title = `The Best ${CAT_TYPE[blogPost?.category]?.trainers} in ${cityText} of ${thisYear}`;
    const titleOtherCities = `See the Best ${CAT_TYPE[blogPost?.category]?.trainers} in Other Cities Near ${blogPost?.city}`;
    const titleOtherCategories = `See the Best Dog Trainers by Type in ${blogPost?.city}`;

    return (
        <div className="mx-2 mx-md-0">
            <h1 className="snif-m1 snif-l1-pc snif-semibold snif-bold-pc mb-3 mb-md-4">{title}</h1>
            <BlogSubDetail {...{ blogPost, updatedAt, type: BLOG_TYPES.DOG_TRAINERS.id }} />
            <div className="mb-4">
                <p className={bDog('p-lora')}>
                    Welcome to the Sniffspot list of top-rated dog trainers for {blogPost?.category} in {cityText}. Based on feedback and votes from
                    our {blogPost?.category} community, we've curated a list of top-rated trainers in the {blogPost?.city} area.
                </p>
                <ul className={bDog('list')}>
                    {blogPost?.trainers?.map((res, i) => (
                        <React.Fragment key={i}>
                            <RenderProfile {...{ res, useStateSSR }} />
                            <Divider />
                        </React.Fragment>
                    ))}
                </ul>
                <div className={bDog('long-desc')} dangerouslySetInnerHTML={{ __html: blogPost?.topSectionDesc }} />
                <Banner1 />
                {blogPost?.cities?.length > 0 && (
                    <>
                        <RenderListCity {...{ ...blogPost, myTitle: titleOtherCities }} />
                        <Divider />
                    </>
                )}
                {blogPost?.categories?.length > 0 && (
                    <>
                        <RenderListCategory {...{ ...blogPost, myTitle: titleOtherCategories }} />
                        <Divider />
                    </>
                )}
                <RenderFaqs {...{ ...blogPost, faqContent }} />
                <Banner2 />
            </div>
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_TRAINERS.id }} noMargin />
            <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
        </div>
    );
};

const RenderAllTestimonials = ({ blogPost, handleWriteTesti, reviews, handleLoadMoreReviews }) => {
    const canLoadMore = blogPost?.reviewsCount > reviews.length;

    return (
        <div>
            <p className={bDog('p-lora').mix('mb-1')}>Support your dog trainer by sharing your story</p>
            <SniffButton size="lg" onClick={handleWriteTesti}>
                Write a testimonial
            </SniffButton>
            <Divider />
            {blogPost?.reviewsCount > 0 ? (
                <>
                    <div className={bDog('testimonials_list')}>
                        {reviews.map((rev) => (
                            <div key={rev.id}>
                                <p className={bDog('p-lora')}>“{rev.testimonial}”</p>
                                <p className="snif-p snif-semibold mt-2">
                                    — {rev.providedBy?.nameAndInital || getFirstNameInitial(rev.reviewerName)}
                                </p>
                            </div>
                        ))}
                    </div>
                    {canLoadMore && (
                        <SniffButton size="lg" color="secondary" className="mt-3" block="mobile" onClick={handleLoadMoreReviews}>
                            Load more testimonials
                        </SniffButton>
                    )}
                </>
            ) : (
                <p className={bDog('p-lora')}>
                    No testimonials yet. Be the first to{' '}
                    <a
                        className="text-primary text-underline"
                        href={RouteFormatter.testiNewDogTrainers({ slug: blogPost?.slug })}
                        onClick={handleWriteTesti}
                    >
                        write one!
                    </a>
                </p>
            )}
        </div>
    );
};

const RenderTestimonials = ({ blogPost, isMobile, reviews, handleLoadMoreReviews, toggleAllTestiModal, handleWriteTesti }) => {
    const canLoadMore = blogPost?.reviewsCount > reviews.length;

    return (
        <div className={bDog('testimonials')}>
            <div className={bDog('testimonials_title')} onClick={isMobile ? toggleAllTestiModal : () => {}}>
                <h2 className="snif-l2 snif-semibold">Testimonials</h2>
                <span className="snif-p text-primary d-none d-md-block c-pointer" onClick={toggleAllTestiModal}>
                    See all testimonials
                </span>
                <span className={bDog('testimonials_right')}>
                    <SvgChevronRight />
                </span>
            </div>
            {blogPost?.reviewsCount > 0 ? (
                <>
                    <div className={bDog('testimonials_list')}>
                        {reviews.map((rev) => (
                            <div key={rev.id}>
                                <p className={bDog('p-lora')}>“{rev.testimonial}”</p>
                                <p className="snif-p snif-semibold mt-2">
                                    — {rev.providedBy?.nameAndInital || getFirstNameInitial(rev.reviewerName)}
                                </p>
                            </div>
                        ))}
                    </div>
                    {canLoadMore && (
                        <SniffButton size="lg" color="secondary" className="my-3 my-md-5" block="mobile" onClick={handleLoadMoreReviews}>
                            Load more testimonials
                        </SniffButton>
                    )}
                </>
            ) : (
                <p className={bDog('p-lora')}>
                    No testimonials yet. Be the first to{' '}
                    <a
                        className="text-primary text-underline"
                        href={RouteFormatter.testiNewDogTrainers({ slug: blogPost?.slug })}
                        onClick={handleWriteTesti}
                    >
                        write one!
                    </a>
                </p>
            )}
            <p className={bDog('p-lora').mix('mt-3 mt-md-5')}>
                Support your dog trainer by{' '}
                <a
                    className="text-primary text-underline"
                    href={RouteFormatter.testiNewDogTrainers({ slug: blogPost?.slug })}
                    onClick={handleWriteTesti}
                >
                    writing a testimonial.
                </a>
            </p>
            <Divider />
        </div>
    );
};

const RenderContentProfile = ({ blogPost, reviews, handleLoadMoreReviews, useStateSSR }) => {
    const isMobile = useMobileScreen();
    const [allTestiModal, setAllTestiModal] = useState(false);
    const mainTitle = blogPost?.fullName;
    const avatarUrl = ImageHelper.getUrlFromOptions(blogPost?.avatar, `fit-in/${IMG_RESIZES.x512}`);
    const web = blogPost?.website || '';
    const fixWeb = web ? (web.startsWith('http') ? web : `http://${web}`) : '';
    const myCity = !blogPost?.city || blogPost?.city?.toLowerCase() == 'none' ? blogPost?.state : blogPost?.city;
    const certs = blogPost?.certifications?.join(', ');
    const recTrainersTitle = `See The Best Dog Trainers In ${myCity}`;
    const recCitiesTitle = `See The Best Dog Trainers In Other Cities Near ${myCity}`;
    const resCatTitle = `See The Best Dog Trainers By Type In ${myCity}`;
    const myCountryRanks = countryRankP.filter((r) => blogPost[r] != null);
    const myCityRanks = cityRankP.filter((r) => blogPost[r] != null);
    const citySlug = blogPost?.city && `${escapeString(blogPost?.city).replaceAll('.', '')}-${getInitState(blogPost?.state)?.toLowerCase()}`;

    const toggleAllTestiModal = () => setAllTestiModal((m) => !m);

    const handleWriteTesti = (e) => {
        e.preventDefault();
        useStateSSR && CookieService.default.set(COOKIE_PARAM_NAME.SIGN_UP_FROM, SIGN_UP_SRC.DOGTRAINERS_WEB, { expires: 1 });
        window.location = RouteFormatter.testiNewDogTrainers({ slug: blogPost?.slug });
    };

    return (
        <div className="mx-2 mx-md-0">
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_TRAINERS.id }} />
            <div className="mb-4">
                <div className={bDog('profile-header')}>
                    <PictureSet
                        className={bDog('profile-header_logo')}
                        imgs={avatarUrl}
                        width={250}
                        height={250}
                        white
                        alt={`${blogPost?.fullName} Photo`}
                    />
                    <div>
                        <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc text-capitalize">{mainTitle}</h1>
                        <div className={bDog('profile-header_content')}>
                            <p className="mb-1">Dog trainer in {blogPost?.city}</p>
                            <RenderRanks {...{ ...blogPost, citySlug }} />
                            <RenderListRankCountry {...{ blogPost, myCountryRanks }} />
                            <RenderListRankCity {...{ blogPost, myCityRanks, citySlug }} />
                            <p className="text-primary my-1">
                                <a href={fixWeb} target="_blank" rel="noopener noreferrer">
                                    {fixWeb}
                                </a>
                            </p>
                            {blogPost?.serviceAreas && (
                                <>
                                    <p className="snif-bold">Service areas:</p>
                                    <p>{blogPost?.serviceAreas}</p>
                                </>
                            )}
                            <p className={bDog('p-lora text-deep mt-2')}>Support your dog trainer by sharing your story</p>
                            <a href={RouteFormatter.testiNewDogTrainers({ slug: blogPost?.slug })} onClick={handleWriteTesti}>
                                <SniffButton size="lg" className="mt-1">
                                    Write a testimonial
                                </SniffButton>
                            </a>
                        </div>
                    </div>
                </div>
                <div className={bDog('p-lora').mix('mt-3')} dangerouslySetInnerHTML={{ __html: blogPost?.bio }} />
                {blogPost?.certifications?.length > 0 && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>Certifications:</h3>
                        <p className={bDog('p-lora')}>{certs}</p>
                    </>
                )}
                {blogPost?.services?.length > 0 && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>Dog training services include</h3>
                        <ul className={bDog('p-lora')}>
                            {blogPost.services.map((s, i) => (
                                <li key={i}>{s}</li>
                            ))}
                        </ul>
                    </>
                )}
                {blogPost?.servicesDetails && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>More info about {blogPost.fullName}'s dog training:</h3>
                        <p className={bDog('p-lora')}>{blogPost.servicesDetails}</p>
                    </>
                )}
                {blogPost?.behavioralIssues && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>Behavioral issue focus:</h3>
                        <p className={bDog('p-lora')}>{blogPost.behavioralIssues}</p>
                    </>
                )}
                {blogPost?.trainingProcess && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>Dog training methods:</h3>
                        <p className={bDog('p-lora')}>{blogPost.trainingProcess}</p>
                    </>
                )}
                {blogPost?.whyDoYouBecomeTrainer && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>Why I became a dog trainer:</h3>
                        <p className={bDog('p-lora')}>{blogPost.whyDoYouBecomeTrainer}</p>
                    </>
                )}
                {blogPost?.favoritePartInBeingTrainer && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>My favorite part of being a dog trainer:</h3>
                        <p className={bDog('p-lora')}>{blogPost.favoritePartInBeingTrainer}</p>
                    </>
                )}
                {blogPost?.firstTrainerTip && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>My #1 dog training tip:</h3>
                        <p className={bDog('p-lora')}>{blogPost.firstTrainerTip}</p>
                    </>
                )}
                <Divider />
                <RenderTestimonials {...{ blogPost, reviews, isMobile, toggleAllTestiModal, handleWriteTesti, handleLoadMoreReviews }} />
                {blogPost?.trainers?.length > 0 && (
                    <>
                        <RenderListTrainer myTitle={recTrainersTitle} {...{ ...blogPost }} />
                        <Divider />
                    </>
                )}
                {blogPost?.cities?.length > 0 && (
                    <>
                        <RenderListCity myTitle={recCitiesTitle} {...{ ...blogPost }} />
                        <Divider />
                    </>
                )}
                {blogPost?.categories?.length > 0 && (
                    <>
                        <RenderListCategory myTitle={resCatTitle} {...{ ...blogPost }} />
                    </>
                )}
                <Banner1 />
            </div>
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_TRAINERS.id }} noMargin />
            <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
            {isMobile ? (
                <MobileSubPage title="All testimonials" show={allTestiModal} onBack={toggleAllTestiModal}>
                    <RenderAllTestimonials {...{ blogPost, handleWriteTesti, reviews, handleLoadMoreReviews }} />
                </MobileSubPage>
            ) : (
                <ModalMobile title="All testimonials" isOpen={allTestiModal} toggle={toggleAllTestiModal} className="all-testi-modal">
                    <RenderAllTestimonials {...{ blogPost, handleWriteTesti, reviews, handleLoadMoreReviews }} />
                </ModalMobile>
            )}
        </div>
    );
};

const RenderMainContent = (props) => {
    const blogPost = props.blogPost;
    const today = new Date();
    const thisYear = today.getFullYear();

    if (blogPost?.pageType == types.STATE) {
        return <RenderContentState {...{ ...props, thisYear }} />;
    } else if (blogPost?.pageType == types.CITY) {
        return <RenderContentCity {...{ ...props, thisYear }} />;
    } else if (blogPost?.pageType == types.CATEGORY) {
        return <RenderContentCategory {...{ ...props, thisYear }} />;
    } else if (blogPost?.pageType == types.CATEGORY_CITY) {
        return <RenderContentCategoryCity {...{ ...props, thisYear }} />;
    } else if (blogPost?.pageType == types.PROFILE) {
        return <RenderContentProfile {...{ ...props, thisYear }} />;
    }

    return <RenderContentCountry {...{ ...props, thisYear }} />;
};

const updatedAt = 'Dec 2, 2025';

const DogTrainers = ({ dataSSR, categoriesSSR, useStateSSR }) => {
    const isMobile = useMobileScreen();
    const { data: clientData, loading } = useStateSSR ? {} : useGetDogTrainersPage.default(window?.location?.pathname?.split('/').slice(3).join('/'));
    const { data: clientCat, loading: loadingCat } = useStateSSR ? {} : useGetBlogCategories.default();
    const { getTrainersTestiPaged } = useStateSSR ? {} : useGetDogTrainersTestiPagination.default();
    const blogPost = dataSSR || clientData || {};
    const blogCategories = categoriesSSR || clientCat || [];
    const recentPosts = blogPost?.recentPosts || [];
    const previousPost = blogPost?.previousPost;
    const nextPost = blogPost?.nextPost;
    const faqContent = blogPost?.faqs?.length > 0 ? blogPost.faqs : DEFAULT_FAQS || DEFAULT_FAQS;
    const listBread = getBread({ ...blogPost });
    const isLoading = loading || loadingCat;
    const [reviews, setReviews] = useState(blogPost?.reviews || []);
    const [page, setPage] = useState(1);

    const handleLoadMoreReviews = async () => {
        try {
            const nextPage = page + 1;
            const res = await getTrainersTestiPaged({ variables: { slug: blogPost.slug, page: nextPage, perPage: 10 } });
            const newReviews = res?.data?.dogTrainersPage?.reviews || [];
            setReviews((prevReviews) => [...prevReviews, ...newReviews]);
            setPage(nextPage);
        } catch (error) {
            console.warn(error);
        }
    };

    const handleGo = (e, slug) => {
        e.preventDefault();
        e.stopPropagation();
        window.location = slug;
    };

    useEffect(() => {
        if (!clientData) {
            return;
        }

        setReviews(clientData?.reviews || []);
    }, [clientData]);

    return (
        <>
            {!useStateSSR && (
                <Helmet>
                    <title>{blogPost?.metaTitle}</title>
                    <meta name="description" content={blogPost?.metaDescription} />
                </Helmet>
            )}
            <div className={`${b()} ${bDog()}`}>
                <BlogTopSticky {...{ useStateSSR }} />
                <BlogTopNav />
                <div className="d-none d-md-block b-bot mb-3" />
                <div className="desktop-container">
                    <div className="mx-2 mx-md-0 mt-1">
                        <SpotBreadcrumbs
                            {...{
                                listBread,
                                SSR: !!useStateSSR,
                                general: true,
                            }}
                        />
                    </div>
                </div>
                <div className={b('content').mix('desktop-container')}>
                    {isLoading ? (
                        <Preloader full />
                    ) : (
                        <RenderMainContent {...{ blogPost, reviews, handleLoadMoreReviews, faqContent, useStateSSR }} />
                    )}
                    <BlogSideBar {...{ blogPost, useStateSSR }} />
                </div>
                <div className="desktop-container">
                    <div className={b('prev-next-post')}>
                        {previousPost && (
                            <div className={b('prev-next-post_prev')} onClick={(e) => handleGo(e, previousPost.slug)}>
                                <SvgChevronLeft />
                                <div>
                                    <p className="snif-xs snif-bold text-primary mb-1 pb-05 mb-md-2 pb-md-0">
                                        <a href={previousPost.slug}>PREVIOUS POST</a>
                                    </p>
                                    <BlogArticleSimple {...{ data: previousPost, mobileSquare: true, useStateSSR }} />
                                </div>
                            </div>
                        )}
                        {nextPost && (
                            <div className={b('prev-next-post_next')} onClick={(e) => handleGo(e, nextPost.slug)}>
                                <div>
                                    <p className="snif-xs snif-bold text-primary mb-1 pb-05 mb-md-2 pb-md-0">
                                        <a href={nextPost.slug}>NEXT POST</a>
                                    </p>
                                    <BlogArticleSimple {...{ data: nextPost, mobileSquare: true, useStateSSR }} />
                                </div>
                                <SvgChevronRight />
                            </div>
                        )}
                    </div>
                    <div className="mx-2 mx-md-0">
                        <h2 className="snif-m1 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5">Most recent articles</h2>
                        <div className={b('recent')}>
                            {recentPosts.map((v) => (
                                <Fragment key={v.id}>
                                    <BlogArticleSimple data={v} {...{ useStateSSR }} />
                                    <BlogArticleDesktopLow data={v} {...{ useStateSSR }} />
                                </Fragment>
                            ))}
                        </div>
                        <div className="d-md-none">
                            <h5 className="snif-m2 snif-semibold mb-3">Related articles</h5>
                            {blogPost?.relatedPosts?.map((v) => (
                                <BlogArticleSimple key={v.id} data={v} {...{ useStateSSR }} />
                            ))}
                        </div>
                        <h2 className="snif-m1 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5 pt-3 pt-md-0">Top dog guides per area</h2>
                        <BlogCategories categories={blogCategories} />
                    </div>
                </div>
                <BlogFooter {...{ isMobile, useStateSSR }} />
            </div>
            {!useStateSSR && <FooterNew isShowCities={false} />}
        </>
    );
};

export default DogTrainers;
